import { Badge } from "@/components/ui/badge";
import { Plane } from "lucide-react";
import { fmtChicagoTime } from "@/utils/timezone";

function getStatusStyle(status, isDelayed) {
  if (status === "landed") return { border: "border-green-300 bg-green-50", icon: "text-green-600", badge: "bg-green-600" };
  if (status === "active") return { border: "border-blue-300 bg-blue-50", icon: "text-blue-600", badge: "bg-blue-600" };
  if (isDelayed || status === "delayed") return { border: "border-red-300 bg-red-50", icon: "text-red-600", badge: "bg-red-600" };
  if (status === "cancelled") return { border: "border-red-200 bg-red-50", icon: "text-red-600", badge: "bg-red-800" };
  return { border: "border-gray-200 bg-white", icon: "text-gray-500", badge: "bg-gray-500" };
}

function formatTime(isoStr) {
  if (!isoStr) return "";
  try { return fmtChicagoTime(isoStr); }
  catch { return isoStr; }
}

export function FlightCard({ flight }) {
  const style = getStatusStyle(flight.status, flight.is_delayed);
  const label = flight.status === "not_found" ? "NOT FOUND" : (flight.status || "UNKNOWN").toUpperCase();

  return (
    <div className={"rounded-xl border-2 p-4 " + style.border} data-testid={"flight-card-" + flight.flight_number}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Plane className={"w-5 h-5 " + style.icon} />
          <span className="font-bold text-lg">{flight.flight_number}</span>
        </div>
        <Badge className={style.badge + " text-white"}>{label}</Badge>
      </div>

      {flight.airline ? <p className="text-sm text-muted-foreground mb-2">{flight.airline}</p> : null}

      <FlightLeg label="Departure" data={flight.departure} />
      <FlightLeg label="Arrival" data={flight.arrival} isArrival delay={flight.delay_minutes} isDelayed={flight.is_delayed} />

      <PassengerList passengers={flight.passengers} />
      {flight.travel_date ? <p className="text-[10px] text-muted-foreground mt-2">Travel Date: {(() => {
        const d = new Date(flight.travel_date + "T12:00:00");
        if (isNaN(d)) return flight.travel_date;
        const wd = d.toLocaleDateString('en-US', { weekday: 'long' });
        return `${wd}, ${flight.travel_date}`;
      })()}</p> : null}
    </div>
  );
}

function FlightLeg({ label, data, isArrival, delay, isDelayed }) {
  if (!data) return null;
  const wrapClass = isArrival ? "text-xs space-y-1 mb-2 p-2 rounded-lg bg-white/70" : "text-xs space-y-1 mb-2";
  const airport = (data.airport || "") + " (" + (data.iata || "") + ")";
  const termInfo = [
    data.terminal ? "Terminal " + data.terminal : "",
    data.gate ? "Gate " + data.gate : "",
    data.baggage ? "Baggage " + data.baggage : ""
  ].filter(Boolean).join(" | ");

  return (
    <div className={wrapClass}>
      <p className="font-medium">{label}: {airport}</p>
      {termInfo ? <p className="text-muted-foreground">{termInfo}</p> : null}
      {data.scheduled ? <p>Scheduled: {formatTime(data.scheduled)}</p> : null}
      {isArrival && data.estimated && data.estimated !== data.scheduled ? (
        <p className={"font-semibold " + (isDelayed ? "text-red-600" : "text-green-600")}>
          Estimated: {formatTime(data.estimated)}{delay > 0 ? " (+" + delay + " min delay)" : ""}
        </p>
      ) : null}
    </div>
  );
}

function PassengerList({ passengers }) {
  if (!passengers || passengers.length === 0) return null;
  return (
    <div className="mt-2 pt-2 border-t">
      <p className="text-xs font-medium text-muted-foreground mb-1">Passengers:</p>
      <div className="flex flex-wrap gap-1">
        {passengers.map((name, j) => (
          <Badge key={j} variant="outline" className="text-[10px]">{name}</Badge>
        ))}
      </div>
    </div>
  );
}
